import * as turf from '@turf/turf';
import { APP } from '../state.js';
import { toast } from '../ui/toast.js';

let clickHandler = null;
let dblHandler   = null;
let moveHandler  = null;
let areaMode = 'polygon';     // cycles 'polygon' → 'rectangle' → 'circle'
let areaPts  = [];             // active vertices for the current mode

export function activateMeasure(map) {
  if (APP.activeTool === 'measure') {
    APP.activeTool = null;
    APP.measurePts = [];
    map.getCanvas().style.cursor = '';
    document.getElementById('tool-measure').classList.remove('active');
    map.getSource('measure').setData({ type: 'FeatureCollection', features: [] });
    if (clickHandler) {
      map.off('click', clickHandler);
      clickHandler = null;
    }
    return;
  }

  APP.activeTool = 'measure';
  APP.measurePts = [];
  map.getCanvas().style.cursor = 'crosshair';
  document.getElementById('tool-measure').classList.add('active');
  toast('Click points to measure distance. Click tool again to finish.');

  clickHandler = e => measureClick(map, e);
  map.on('click', clickHandler);
}

function measureClick(map, e) {
  if (APP.activeTool !== 'measure') return;

  APP.measurePts.push([e.lngLat.lng, e.lngLat.lat]);
  const feats = APP.measurePts.map(p => ({
    type: 'Feature',
    geometry: { type: 'Point', coordinates: p }
  }));

  if (APP.measurePts.length > 1) {
    const line = { type: 'Feature', geometry: { type: 'LineString', coordinates: APP.measurePts } };
    feats.push(line);
    const dist = turf.length(line, { units: 'kilometers' });
    toast(`Distance: ${dist.toFixed(2)} km (${(dist * 0.621371).toFixed(2)} mi)`);
  }

  map.getSource('measure').setData({ type: 'FeatureCollection', features: feats });
}


// =========================================================================
// AREA — polygon (freehand), rectangle, circle
// =========================================================================

const AREA_MODES = ['polygon', 'rectangle', 'circle'];
const AREA_LABEL = {
  polygon:   'Polygon (click to add vertices, double-click to close)',
  rectangle: 'Rectangle (click two opposite corners)',
  circle:    'Circle (click center, then a point on the radius)',
};

/**
 * Toggle area-measure mode. Each click cycles to the next mode (or
 * deactivates if cycling past the last). Mirrors how `activateMeasure`
 * works for line distance.
 */
export function activateMeasureArea(map) {
  // If we're already in area-measure mode, advance to the next sub-mode
  if (APP.activeTool === 'measure-area') {
    const next = AREA_MODES[(AREA_MODES.indexOf(areaMode) + 1) % AREA_MODES.length];
    if (AREA_MODES.indexOf(areaMode) === AREA_MODES.length - 1) {
      // cycled through all — turn off
      _deactivateArea(map);
      return;
    }
    areaMode = next;
    areaPts = [];
    map.getSource('measure').setData({ type: 'FeatureCollection', features: [] });
    toast(AREA_LABEL[areaMode]);
    return;
  }

  // Cancel any other active tool and switch on
  if (APP.activeTool === 'measure') {
    APP.activeTool = null;
    APP.measurePts = [];
    document.getElementById('tool-measure')?.classList.remove('active');
    if (clickHandler) { map.off('click', clickHandler); clickHandler = null; }
    map.getSource('measure').setData({ type: 'FeatureCollection', features: [] });
  }

  APP.activeTool = 'measure-area';
  areaMode = 'polygon';
  areaPts = [];
  map.getCanvas().style.cursor = 'crosshair';
  document.getElementById('tool-area')?.classList.add('active');
  toast(`${AREA_LABEL[areaMode]} · click again to switch mode`);

  clickHandler = e => areaClick(map, e);
  dblHandler   = e => areaDblClick(map, e);
  moveHandler  = e => areaMouseMove(map, e);
  map.on('click',    clickHandler);
  map.on('dblclick', dblHandler);
  map.on('mousemove', moveHandler);
  map.doubleClickZoom.disable();
}

function _deactivateArea(map) {
  APP.activeTool = null;
  areaPts = [];
  map.getCanvas().style.cursor = '';
  document.getElementById('tool-area')?.classList.remove('active');
  if (clickHandler)  { map.off('click', clickHandler); clickHandler = null; }
  if (dblHandler)    { map.off('dblclick', dblHandler); dblHandler = null; }
  if (moveHandler)   { map.off('mousemove', moveHandler); moveHandler = null; }
  map.doubleClickZoom.enable();
  map.getSource('measure').setData({ type: 'FeatureCollection', features: [] });
}

function areaClick(map, e) {
  if (APP.activeTool !== 'measure-area') return;
  const pt = [e.lngLat.lng, e.lngLat.lat];

  if (areaMode === 'polygon') {
    areaPts.push(pt);
    renderAreaPreview(map);
    return;
  }
  if (areaMode === 'rectangle') {
    areaPts.push(pt);
    if (areaPts.length === 2) {
      finishRectangle(map);
    } else {
      renderAreaPreview(map);
    }
    return;
  }
  if (areaMode === 'circle') {
    areaPts.push(pt);
    if (areaPts.length === 2) {
      finishCircle(map);
    } else {
      renderAreaPreview(map);
    }
    return;
  }
}

function areaDblClick(map, e) {
  // Double-click closes a polygon
  if (APP.activeTool !== 'measure-area' || areaMode !== 'polygon') return;
  if (areaPts.length < 3) return;
  e.preventDefault();
  finishPolygon(map);
}

function areaMouseMove(map, e) {
  // Live preview for rectangle / circle while user picks the second point
  if (APP.activeTool !== 'measure-area' || areaPts.length !== 1) return;
  if (areaMode === 'polygon') return;

  const cursorPt = [e.lngLat.lng, e.lngLat.lat];
  const feats = previewFeatures(areaPts[0], cursorPt, areaMode);
  map.getSource('measure').setData({ type: 'FeatureCollection', features: feats });
}

function previewFeatures(p1, p2, mode) {
  if (mode === 'rectangle') {
    const ring = rectangleRing(p1, p2);
    return [{ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] } }];
  }
  if (mode === 'circle') {
    const radiusKm = turf.distance(turf.point(p1), turf.point(p2), { units: 'kilometers' });
    return [turf.circle(p1, radiusKm, { units: 'kilometers', steps: 64 })];
  }
  return [];
}

function rectangleRing(a, b) {
  const minX = Math.min(a[0], b[0]);
  const maxX = Math.max(a[0], b[0]);
  const minY = Math.min(a[1], b[1]);
  const maxY = Math.max(a[1], b[1]);
  return [
    [minX, minY], [maxX, minY], [maxX, maxY], [minX, maxY], [minX, minY],
  ];
}

function renderAreaPreview(map) {
  // For polygon: vertices + tentative ring back to first point if 3+
  // For rectangle/circle with 1 click: just the seed point
  const feats = areaPts.map(p => ({
    type: 'Feature', geometry: { type: 'Point', coordinates: p },
  }));
  if (areaMode === 'polygon' && areaPts.length >= 3) {
    feats.push({
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [[...areaPts, areaPts[0]]],
      },
    });
  } else if (areaMode === 'polygon' && areaPts.length === 2) {
    feats.push({
      type: 'Feature',
      geometry: { type: 'LineString', coordinates: areaPts },
    });
  }
  map.getSource('measure').setData({ type: 'FeatureCollection', features: feats });
}

function finishPolygon(map) {
  const ring = [...areaPts, areaPts[0]];
  const poly = turf.polygon([ring]);
  reportArea(poly);
  map.getSource('measure').setData({
    type: 'FeatureCollection',
    features: [poly, ...areaPts.map(p => ({ type: 'Feature', geometry: { type: 'Point', coordinates: p } }))],
  });
  areaPts = [];
}

function finishRectangle(map) {
  const ring = rectangleRing(areaPts[0], areaPts[1]);
  const poly = turf.polygon([ring]);
  reportArea(poly);
  map.getSource('measure').setData({ type: 'FeatureCollection', features: [poly] });
  areaPts = [];
}

function finishCircle(map) {
  const radiusKm = turf.distance(
    turf.point(areaPts[0]), turf.point(areaPts[1]),
    { units: 'kilometers' },
  );
  const poly = turf.circle(areaPts[0], radiusKm, { units: 'kilometers', steps: 64 });
  reportArea(poly, ` · radius ${radiusKm.toFixed(2)} km`);
  map.getSource('measure').setData({ type: 'FeatureCollection', features: [poly] });
  areaPts = [];
}

function reportArea(polygon, suffix = '') {
  const sqM = turf.area(polygon);
  const sqKm = sqM / 1_000_000;
  const ha   = sqM / 10_000;
  const human =
    sqKm >= 1   ? `${sqKm.toFixed(2)} km²`
    : ha   >= 1 ? `${ha.toFixed(2)} ha`
    :             `${sqM.toFixed(0)} m²`;
  toast(`Area: ${human}${suffix}`);
}
